import type {CurrencyCode, ObjectFieldsAssertion, Values} from '@wix/devzai-utils-common';
import {arrayRemoveNullValues, currencyFormatPrice, currencyGetAllCodes, urlJoin} from '@wix/devzai-utils-common';
import type {WixProduct, WixRicosDocument, WixStoreProductPhysicalEditPayloadFromV3} from '@wix/devzai-common-wix';
import {wixProductMediaTryConvertToWixMediaResource} from '@wix/devzai-common-wix';
import type {DoppeActionPriceData} from './doppe-action-price-data';
import type {DoppeActionPrice} from './doppe-action-price';

export const DoppeDtoStoreProductType = {
    Unspecified: 'unspecified_product_type',
    Physical: 'physical',
    Digital: 'digital'
} as const;

export type DoppeDtoStoreProductType = Values<typeof DoppeDtoStoreProductType>;

export namespace DoppeDtoStoreProductType {
    export type Specified = Exclude<DoppeDtoStoreProductType, typeof DoppeDtoStoreProductType.Unspecified>;
}

export interface DoppeDtoStoreProduct {
    id: string;
    slug?: string;
    name: string;
    description: string;
    descriptionPlainText: string;
    media?: WixProduct.Media;
    price: DoppeDtoStoreProduct.PriceData;
    productPageUrl?: string;
    discount?: WixProduct.Discount;
    stock?: WixProduct.Stock;
    productType: DoppeDtoStoreProductType;
    ribbon?: WixProduct['ribbon'];
    productOptions?: WixProduct.ProductOption[];
    additionalInfoSections?: WixProduct.AdditionalInfoSection[];
    customTextFields?: WixProduct.CustomTextField[];
    /**
     * Whether variants are managed for this product.
     * When true, the product has variant-level inventory and pricing.
     */
    manageVariants?: boolean;
    /**
     * Whether the product is visible in the online store.
     * When false, the product is hidden from customers.
     * Source: Wix Stores API `visible` field.
     */
    visible?: boolean;
    /**
     * Digital download file id (Wix Media Manager) for digital products, when known from Stores API.
     */
    digitalFileId?: string;
    /**
     * V3 catalog rich description (Ricos) for in-app editing; plain text remains in `description` / `descriptionPlainText`.
     */
    descriptionRicosDocument?: WixRicosDocument;
    /**
     * V3 physical products: options/variants (+ description doc) for in-app edit when conversion succeeds.
     */
    physicalProductV3EditPayload?: WixStoreProductPhysicalEditPayloadFromV3;
    /**
     * V3 subscription details embedded on the product.
     */
    subscriptionDetails?: DoppeDtoStoreProduct.SubscriptionDetails;
}

export namespace DoppeDtoStoreProduct {
    export interface SubscriptionDetails {
        subscriptions: SubscriptionOption[];
        allowOneTimePurchases: boolean;
    }

    export interface SubscriptionOption {
        id?: string;
        title: string;
        description?: string;
        frequency: 'WEEK' | 'MONTH' | 'YEAR';
        interval: number;
        autoRenewal?: boolean;
        billingCycles?: number;
        visible?: boolean;
        discount?: {
            type: 'AMOUNT' | 'PERCENT';
            amountOff?: string;
            percentOff?: number;
        };
    }
}

export namespace DoppeDtoStoreProduct {
    export type Id = DoppeDtoStoreProduct['id'];

    export type PriceData = WixProduct.PriceData;

    export type ProductType = DoppeDtoStoreProductType;

    export type ForUpdate = Pick<DoppeDtoStoreProduct, 'id' | 'price' | 'name' | 'descriptionPlainText' | 'description' | 'media' | 'productType' | 'digitalFileId' | 'descriptionRicosDocument'>

    export type ForCreation = Pick<DoppeDtoStoreProduct, 'price' | 'name'| 'descriptionPlainText' | 'description' | 'media' | 'productType'>

    /**
     * A product variant representing a specific combination of options.
     */
    export interface Variant {
        /**
         * Variant ID.
         */
        id: string | null;
        /**
         * Mapping of option names to selected choice values.
         * Example: { "Color": "Blue", "Size": "XL" }
         */
        choices: Record<string, string>;
        /**
         * Whether this variant is in stock.
         */
        inStock: boolean;
        /**
         * Whether this variant is visible.
         */
        visible: boolean;
        /**
         * Variant-specific price data if different from the main product.
         */
        priceData?: PriceData;
        /**
         * SKU for this variant.
         */
        sku?: string;
    }
}

export function doppeStoreProductResolveUrl(productPageUrl: WixProduct.PageUrl) {
    if (productPageUrl?.base && productPageUrl?.path) {
        return urlJoin(productPageUrl.base,productPageUrl.path);
    }

    return undefined;
}

export const DoppeDtoStoreProductForCreationFieldsAssertions: ObjectFieldsAssertion<DoppeDtoStoreProduct.ForCreation> = {
    name: assert => assert.isString(),
    description: assert => assert.isString(),
    descriptionPlainText: assert => assert.isString(),
    price: assert => assert.usingAssertionFunction(() => {
        // TODO
    }),
    media: assert => assert.optional.usingAssertionFunction(() => {
        // TODO
    }),
    productType: assert => assert.usingAssertionFunction(() => {
        // TODO
    }),
}

export const DoppeDtoStoreProductForUpdateFieldsAssertions: ObjectFieldsAssertion<DoppeDtoStoreProduct.ForUpdate> = {
    id: assert => assert.isString(),
    name: assert => assert.isString(),
    description: assert => assert.isString(),
    descriptionPlainText: assert => assert.isString(),
    price: assert => assert.usingAssertionFunction(() => {
        // TODO
    }),
    media: assert => assert.optional.usingAssertionFunction(() => {
        // TODO
    }),
    productType: assert => assert.usingAssertionFunction(() => {
        // TODO
    }),
    digitalFileId: assert => assert.optional.isString(),
    descriptionRicosDocument: assert => assert.optional.isAny(),
}

export function doppeDtoStoreProductPriceDataConvertToActionPriceData (price: DoppeDtoStoreProduct.PriceData) : DoppeActionPriceData {
    const actualPrice = doppeDtoStoreProductConvertToFormattedPrice(price, '');
    const discountPrice = doppeDtoStoreProductConvertToFormattedDiscountPrice(price, '');

    return {
        actualPrice: actualPrice,
        discountPrice: actualPrice !== discountPrice ? discountPrice : '',
    }
}

export function doppeDtoStoreProductPriceDataConvertToActionPrice(price: DoppeDtoStoreProduct.PriceData) : DoppeActionPrice {
    return {
        actualPrice: Number.isFinite(price.price) ? price.price as number : 1,
        discountPrice: price.discountedPrice,
        currencyCode: price.currency ? price.currency as CurrencyCode : 'USD'
    }
}

export function doppeDtoProductGetMedia (dtoStoreProduct: DoppeDtoStoreProduct) {
    return arrayRemoveNullValues([
        dtoStoreProduct.media?.mainMedia ? wixProductMediaTryConvertToWixMediaResource(dtoStoreProduct.media?.mainMedia) : null,
    ])
}

export function doppeDtoProductGetMediaItems (dtoStoreProduct: DoppeDtoStoreProduct) {
    return arrayRemoveNullValues(dtoStoreProduct.media?.items?.map(item => wixProductMediaTryConvertToWixMediaResource(item)) ?? [])
}

/**
 * Optional formatting behavior for {@link doppeDtoStoreProductConvertToFormattedPrice} /
 * {@link doppeDtoStoreProductConvertToFormattedDiscountPrice}.
 */
export interface DoppeDtoStoreProductConvertToFormattedPriceOptions {
    /**
     * When `priceData.currency` is missing (common for some V3 catalog payloads),
     * format numeric amounts using this store/site currency.
     */
    fallbackCurrency?: CurrencyCode | null;
}

function doppeDtoStoreProductPriceDataHasNumericPrice(price: number | undefined): boolean {
    return price !== undefined && Number.isFinite(price);
}

function doppeDtoStoreProductPriceDataResolveCurrency(
    priceData: DoppeDtoStoreProduct.PriceData,
    fallbackCurrency?: CurrencyCode | null
): CurrencyCode | undefined {
    const fromProduct = priceData.currency as CurrencyCode | undefined;
    if (fromProduct && currencyGetAllCodes().includes(fromProduct)) {
        return fromProduct;
    }
    if (fallbackCurrency && currencyGetAllCodes().includes(fallbackCurrency)) {
        return fallbackCurrency;
    }
    return undefined;
}

export function doppeDtoStoreProductConvertToFormattedPrice<EmptyValue>(
    priceData: DoppeDtoStoreProduct.PriceData,
    emptyValue: EmptyValue,
    options?: DoppeDtoStoreProductConvertToFormattedPriceOptions
): string | EmptyValue {
    const currency = doppeDtoStoreProductPriceDataResolveCurrency(priceData, options?.fallbackCurrency);

    return doppeDtoStoreProductPriceDataHasNumericPrice(priceData.price) && currency
        ? currencyFormatPrice({
            code: currency,
            price: priceData.price as number
        })
        : (priceData.formatted?.price ?? emptyValue);
}

export function doppeDtoStoreProductConvertToFormattedDiscountPrice<EmptyValue>(
    priceData: DoppeDtoStoreProduct.PriceData,
    emptyValue: EmptyValue,
    options?: DoppeDtoStoreProductConvertToFormattedPriceOptions
): string | EmptyValue {
    const currency = doppeDtoStoreProductPriceDataResolveCurrency(priceData, options?.fallbackCurrency);

    return doppeDtoStoreProductPriceDataHasNumericPrice(priceData.discountedPrice) && currency
        ? currencyFormatPrice({
            code: currency,
            price: priceData.discountedPrice as number
        })
        : (priceData.formatted?.discountedPrice ?? emptyValue);
}

/**
 * Options for creating a dummy store product with localized content and images.
 */
export interface DoppeDtoStoreProductCreateDummyOptions {
    /**
     * Localized product name.
     */
    name: string;
    /**
     * Localized product description.
     */
    description: string;
    /**
     * Product image (WixImageInfo format).
     */
    image: WixProduct.Media['mainMedia'];
    /**
     * Localized option names and choice values for variants.
     */
    options: {
        colorName: string;
        sizeName: string;
        colorBlack: string;
        colorWhite: string;
        colorBlue: string;
        sizeS: string;
        sizeM: string;
        sizeL: string;
    };
}

/**
 * Creates a dummy store product with placeholder content.
 * Used when no actual product is available (e.g., for preview or empty state).
 */
export function doppeDtoStoreProductCreateDummy(
    options: DoppeDtoStoreProductCreateDummyOptions
): DoppeDtoStoreProduct {
    const {
        name,
        description,
        image,
        options: optionLabels
    } = options;

    // Create product options for Color and Size
    const productOptions: WixProduct.ProductOption[] = [
        {
            name: optionLabels.colorName,
            optionType: 'color',
            choices: [
                { value: optionLabels.colorBlack, inStock: true, visible: true },
                { value: optionLabels.colorWhite, inStock: true, visible: true },
                { value: optionLabels.colorBlue, inStock: true, visible: true }
            ]
        },
        {
            name: optionLabels.sizeName,
            optionType: 'drop_down',
            choices: [
                { value: optionLabels.sizeS, inStock: true, visible: true },
                { value: optionLabels.sizeM, inStock: true, visible: true },
                { value: optionLabels.sizeL, inStock: true, visible: true }
            ]
        }
    ];


    return {
        id: 'dummy-product',
        slug: 'dummy-product',
        name: name,
        description: `<p>${description}</p>`,
        descriptionPlainText: description,
        media: image ? {
            mainMedia: image,
            items: [image]
        } : undefined,
        price: {
            price: 99.99,
            currency: 'USD',
            formatted: {
                price: '$99.99'
            }
        },
        productType: DoppeDtoStoreProductType.Physical,
        stock: {
            inStock: true,
            trackInventory: false
        },
        productOptions: productOptions,
        manageVariants: true
    };
}